import {
  Box,
  Heading,
  Flex,
  InputLeftAddon,
  Input,
  InputGroup,
  useToast,
  useDisclosure,
  Text,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import React, { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import { finleyAssert, path as commonPath } from '@finley-technologies/common';
import { FinleyAi, typeguards } from '@finley-technologies/finley-types';
import { ExtractionStruct } from '@finley-technologies/tape-sanitizer';

import { SchemaEditor } from '../../common-frontend/components/SchemaEditor';
import { FinleyAiViewContext, TransformViewContext } from './types';
import {
  DeleteIconButton,
  UploadIconButton,
} from 'packages/common-frontend/components/IconButton';
import { UploadModal } from 'packages/common-frontend/components/ReactFlow/UploadModal';
import { useFinleyAiTransformDirectUpload } from 'packages/finleyAi/hooks/transformDirectUpload';

export const extractValuesOptions: {
  value: FinleyAi.ExtractValuesSubType;
  label: string;
}[] = [{ value: 'extractJson' as const, label: 'Extract Json' }];

export const extractValuesDataTypeOptions: {
  value: FinleyAi.FinleyAiExtractedValueQuestionConfigDataType;
  label: string;
}[] = [
  { value: 'string' as const, label: 'string' },
  { value: 'integer' as const, label: 'integer' },
  { value: 'float' as const, label: 'float' },
  { value: 'boolean' as const, label: 'boolean' },
];

type FinleyAiTransformProps = {
  targetOrganizationId: string;
  isLocked: boolean;
  viewContext: TransformViewContext;
  setViewContext: (value: React.SetStateAction<FinleyAiViewContext>) => void;
};

export const FinleyAiTransform = ({
  targetOrganizationId,
  isLocked,
  setViewContext,
  viewContext,
}: FinleyAiTransformProps) => {
  const toast = useToast();

  const [documentName, setDocumentName] = useState<string>('');
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { directUpload } = useFinleyAiTransformDirectUpload({
    targetOrganizationId,
  });

  const upload = useCallback(
    async (file: File) => {
      if (documentName !== '') {
        try {
          const fileExtension = commonPath.getFileExtension(file.name);

          finleyAssert(
            typeguards.finleyAi.isFinleyAiTransformSupportedFileExtension(
              fileExtension,
            ),
            `[fileExtension:${fileExtension}] is not a supported file extension.`,
          );
          const { s3Bucket, s3Path } = await directUpload({
            file,
            documentName,
            fileExtension,
          });

          setViewContext((vc) => {
            finleyAssert(
              vc.type === 'transform',
              `View context type must be transform, but got ${vc.type}`,
            );
            return {
              ...vc,
              documents: [
                ...vc.documents,
                {
                  documentName,
                  s3Bucket,
                  s3Key: s3Path,
                  extraction: {
                    fileExt: fileExtension,
                  },
                },
              ],
            };
          });
          setDocumentName('');
        } catch (e) {
          toast({
            title: 'Document upload failed. Please see technical assistance.',
            status: 'error',
            position: 'top',
            isClosable: true,
          });
        }
      } else {
        toast({
          title: 'Document Name must be specified.',
          status: 'error',
          position: 'top',
          isClosable: true,
        });
      }
    },
    [directUpload, toast, documentName, setViewContext],
  );

  const handleDelete = (idx: number) => {
    setViewContext((vc) => {
      finleyAssert(
        vc.type === 'transform',
        `View context type must be transform, but got ${vc.type}`,
      );
      const documents = vc.documents.filter(
        (_document, docIdx) => docIdx !== idx,
      );
      return {
        ...vc,
        documents,
      };
    });
  };

  const handleToolChange = useCallback(
    (changedTool: string) => {
      setViewContext((prevState) => ({
        ...prevState,
        tool: changedTool,
      }));
    },
    [setViewContext],
  );

  const jsonSchema = useMemo(() => {
    return z.toJSONSchema(ExtractionStruct, {
      target: 'draft-7',
    });
  }, []);

  return (
    <Box mr="20px" width="100%">
      <Heading as="h4" size="md" mb="3">
        Transform Configuration
      </Heading>

      <Flex mb="3" alignItems="center">
        <Heading as="h4" size="md" mr="3">
          Add Document
        </Heading>
        <UploadIconButton
          ariaLabel="upload"
          label="upload"
          onClick={onOpen}
          isDisabled={isLocked}
        />
        <UploadModal
          modalHeaderText="Upload Document"
          onClose={onClose}
          isOpen={isOpen}
          handleFile={upload}
          allowedExtensions={[]}
        >
          <InputGroup mb="3">
            <InputLeftAddon>Document Name</InputLeftAddon>
            <Input
              type="text"
              placeholder="documentName"
              value={documentName}
              onChange={(e) => {
                setDocumentName(e.target.value);
              }}
            />
          </InputGroup>
        </UploadModal>
      </Flex>

      <Box>
        {viewContext.documents.map((document, idx) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={`${documentName}-{${idx}}`}>
              <Flex alignItems="center">
                <Text>{document.documentName}</Text>
                <DeleteIconButton
                  onClick={() => handleDelete(idx)}
                  isDisabled={isLocked}
                />
              </Flex>

              <Box>
                <SchemaEditor
                  direction="vertical"
                  contents={
                    document.extraction ?? {
                      fileExt: commonPath.getFileExtension(document.s3Key),
                    }
                  }
                  height="300px"
                  nodeId="temp"
                  newConfig={false}
                  schema={jsonSchema}
                  onSave={async (_id: string, newValue: object) => {
                    const parsed = z.parse(ExtractionStruct, newValue);
                    setViewContext((vc) => {
                      finleyAssert(
                        vc.type === 'transform',
                        'View Context must be transform.',
                      );
                      const documents = [...vc.documents];

                      const config = { ...documents[idx] };

                      config.extraction = parsed;

                      documents[idx] = config;

                      return {
                        ...vc,
                        documents,
                      };
                    });
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box width="100%">
        <Heading as="h4" size="md" mr="3" mt="16px" mb="3">
          Tool (jsonschema)
        </Heading>
        <Editor
          value={viewContext.tool}
          onChange={(val) => handleToolChange(val ?? '')}
          theme="vs-dark"
          width="100%"
          height="200px"
        />
      </Box>
    </Box>
  );
};
